/**
 * PeriodSelector
 * Component for selecting morning/afternoon period for Ordem de Chegada clinics
 * Shows capacity info and disables periods when full
 */

import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  LinearProgress,
  Chip,
} from '@mui/material';
import { WbSunny, NightsStay } from '@mui/icons-material';
import type { OrdemChegadaAvailability } from '../../../types/appointment';

interface PeriodSelectorProps {
  availability: OrdemChegadaAvailability | null;
  selectedPeriod: string | null;
  onSelectPeriod: (period: string) => void;
  loading?: boolean;
  selectedDate?: string;
}

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  availability,
  selectedPeriod,
  onSelectPeriod,
  loading = false,
  selectedDate,
}) => {
  if (loading || !availability) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Carregando disponibilidade...' : 'Selecione uma data para ver a disponibilidade'}
        </Typography>
      </Box>
    );
  }

  const periods = [
    {
      key: 'morning',
      label: 'Manhã',
      icon: <WbSunny />,
      data: availability.morning,
    },
    {
      key: 'afternoon',
      label: 'Tarde',
      icon: <NightsStay />,
      data: availability.afternoon,
    },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
      {periods.map(({ key, label, icon, data }) => {
        const isFull = data.capacity > 0 && data.available <= 0;
        const isSelected = selectedPeriod === key;
        const hasCapacity = data.capacity > 0;
        const usagePercent = hasCapacity ? (data.booked / data.capacity) * 100 : 0;

        // Check 3-hour minimum scheduling window
        const isTooSoon = (() => {
          if (!selectedDate || !data.startTime) return false;
          const now = new Date();
          const minTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
          const [year, month, day] = selectedDate.split('-').map(Number);
          const [h, m] = data.startTime.split(':').map(Number);
          const periodStart = new Date(year, month - 1, day, h, m);
          return periodStart < minTime;
        })();
        const isUnavailable = isFull || isTooSoon;

        const timeRange = data.startTime && data.endTime
          ? `${data.startTime} às ${data.endTime}`
          : '';

        return (
          <Card
            key={key}
            variant={isSelected ? 'elevation' : 'outlined'}
            sx={{
              flex: 1,
              border: isSelected ? 2 : 1,
              borderColor: isSelected ? 'primary.main' : isUnavailable ? 'action.disabled' : 'divider',
              opacity: isUnavailable ? 0.6 : 1,
              bgcolor: isSelected ? 'primary.50' : 'background.paper',
            }}
          >
            <CardActionArea
              onClick={() => !isUnavailable && onSelectPeriod(key)}
              disabled={isUnavailable}
              sx={{ p: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {icon}
                <Typography variant="subtitle1" fontWeight={600}>
                  {label}
                </Typography>
                {isTooSoon && !isFull && (
                  <Chip label="Mín. 3h" color="warning" size="small" sx={{ ml: 'auto' }} />
                )}
                {isFull && (
                  <Chip label="Lotado" color="error" size="small" sx={{ ml: 'auto' }} />
                )}
              </Box>

              {timeRange && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {timeRange}
                </Typography>
              )}

              {hasCapacity && (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usagePercent, 100)}
                    color={isFull ? 'error' : usagePercent > 80 ? 'warning' : 'primary'}
                    sx={{ mb: 0.5, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {data.booked} de {data.capacity} vagas ocupadas — {data.available} disponíveis
                  </Typography>
                </>
              )}
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
};

export default PeriodSelector;
